import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from "react";
import {Loader2} from "lucide-react";
import {Button} from "./ui/button";

const ConfirmDialogContext = createContext(null);

export function ConfirmDialogProvider({children}) {
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const resolver = useRef(null);
  const cancelButton = useRef(null);

  const confirm = useCallback((options) => new Promise((resolve) => {
    if (resolver.current) resolver.current(false);
    resolver.current = resolve;
    setBusy(false);
    setDialog({
      title: options.title || "Are you sure?",
      description: options.description || "",
      confirmLabel: options.confirmLabel || "Continue",
      destructive: options.destructive !== false,
      onConfirm: options.onConfirm,
      error: null,
    });
  }), []);

  const close = useCallback((result) => {
    if (busy) return;
    const resolve = resolver.current;
    resolver.current = null;
    setDialog(null);
    setBusy(false);
    resolve?.(result);
  }, [busy]);

  const handleConfirm = async () => {
    if (!dialog || busy) return;
    setBusy(true);
    try {
      const result = await dialog.onConfirm?.();
      if (result?.success === false) {
        throw new Error(result.error || "The action could not be completed.");
      }
      const resolve = resolver.current;
      resolver.current = null;
      setDialog(null);
      resolve?.(true);
    } catch (error) {
      setDialog((current) => ({
        ...current,
        error: error?.message || "The action could not be completed.",
      }));
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!dialog) return undefined;
    cancelButton.current?.focus();
    const handleKeyDown = (event) => {
      if (event.key === "Escape") close(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [dialog, close]);

  return (
    <ConfirmDialogContext.Provider value={confirm}>
      {children}
      {dialog ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/55 p-4"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) close(false);
          }}
        >
          <section
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="confirm-dialog-title"
            aria-describedby={dialog.description ? "confirm-dialog-description" : undefined}
            className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl"
          >
            <h2 id="confirm-dialog-title" className="m-0 text-lg font-semibold text-slate-900">
              {dialog.title}
            </h2>
            {dialog.description ? (
              <p id="confirm-dialog-description" className="mb-0 mt-2 text-sm text-slate-600">
                {dialog.description}
              </p>
            ) : null}
            {dialog.error ? (
              <p role="alert" className="mb-0 mt-3 text-sm text-red-700">
                {dialog.error}
              </p>
            ) : null}
            <div className="mt-6 flex justify-end gap-2">
              <Button
                ref={cancelButton}
                type="button"
                variant="outline"
                disabled={busy}
                onClick={() => close(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant={dialog.destructive ? "destructive" : "default"}
                disabled={busy}
                onClick={handleConfirm}
              >
                {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
                {busy ? "Working…" : dialog.confirmLabel}
              </Button>
            </div>
          </section>
        </div>
      ) : null}
    </ConfirmDialogContext.Provider>
  );
}

export function useConfirmDialog() {
  const confirm = useContext(ConfirmDialogContext);
  if (!confirm) throw new Error("useConfirmDialog must be used within ConfirmDialogProvider.");
  return confirm;
}
